import { useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Box, Button } from "@mui/material";
import useStyles from "./filter-bar.style";
import plusIcon from "@iconify/icons-mdi/plus";
import { InlineIcon } from "@iconify/react";
import { useMyHrUserRolesDataByContract } from "my-hr/hooks";
import { useMyLeaveExaminersName } from "my-leave/hooks";
import { OcCustomTooltip } from "oc/components";
import {
  useOcButtonGroup,
  useOcCurrentUserState,
  useOcIsMobile,
  useOcProperty,
} from "oc/hooks";

type MyLeaveFilterBarProps = {
  tableData: { filterSelected: string[] };
  setTableData: (data: { filterSelected: string[] }) => void;
  loading: boolean;
  onShowNewRequestDialog: (show: boolean) => void;
  disabledNewRequest: boolean;
};

export const MyLeaveFilterBar = ({
  tableData: _tableData,
  setTableData,
  loading,
  onShowNewRequestDialog,
  disabledNewRequest,
}: MyLeaveFilterBarProps) => {
  const { t, i18n } = useTranslation();
  const isMobile = useOcIsMobile();

  const { data: createLeaveRequestIsEnabled } = useOcProperty({
    resource: "myLeave",
    key: "createLeaveRequestIsEnabled",
  });

  const classes = useStyles({ isMobile, createLeaveRequestIsEnabled });

  const buttonsDef = useMemo(() => ["OC_REQUESTED_PAST", "OC_ALL"], []);

  const { OcButtonGroup, selectedButton, handleButtonChange } =
    useOcButtonGroup({ id: "MyLeaveFilterBar", buttonsDef });

  const { data: examinersData } = useMyLeaveExaminersName({
    locale: i18n.language,
  });
  const { contractCode } = useOcCurrentUserState();
  const { data: rolesData } = useMyHrUserRolesDataByContract({
    contract: contractCode,
  });

  const isExaminer = rolesData?.some(
    (item) => item.product === "MyLeave" && item.role === "examiner"
  );
  const hasExaminer = examinersData?.length > 0;
  let isEditButtonDisabled = !hasExaminer;
  if (isExaminer && !hasExaminer) {
    isEditButtonDisabled = false;
  }

  // FIX: Ref to track the previous selectedButton to avoid infinite loops
  const prevSelectedButtonRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    // Only update filterSelected when selectedButton actually changes
    if (prevSelectedButtonRef.current === selectedButton) {
      return;
    }
    prevSelectedButtonRef.current = selectedButton;

    if (createLeaveRequestIsEnabled === "0") {
      if (selectedButton !== buttonsDef[1]) {
        handleButtonChange(1);
      }
      setTableData({ filterSelected: [] });
      return;
    }

    if (selectedButton === buttonsDef[0]) {
      setTableData({ filterSelected: ["requested"] });
    } else {
      setTableData({ filterSelected: [] });
    }
  }, [
    selectedButton,
    createLeaveRequestIsEnabled,
    buttonsDef,
    handleButtonChange,
    setTableData,
  ]);

  return (
    <div className={classes.root}>
      <div className={classes.leftContainer} />
      <Box
        sx={{
          display: isMobile ? "flex" : "unset",
          justifyContent: isMobile ? "center" : "unset",
        }}
      >
        <OcButtonGroup />
      </Box>
      {isMobile && <Box height="36px" />}
      <div className={classes.rightContainer}>
        {createLeaveRequestIsEnabled === "1" && !isEditButtonDisabled && (
          <Button
            id="showDialog"
            name="showDialog"
            variant="contained"
            color="primary"
            startIcon={<InlineIcon icon={plusIcon} />}
            onClick={() => onShowNewRequestDialog(true)}
            disableElevation
            disabled={loading || disabledNewRequest}
          >
            {t("MY_LEAVE_NEW_REQUEST")}
          </Button>
        )}
        {isEditButtonDisabled && (
          <OcCustomTooltip
            title={t("OC_NOT_SET_EXAMINER_INFO")}
            placement="top"
          >
            <Button
              id="showDialog"
              name="showDialog"
              variant="contained"
              startIcon={<InlineIcon icon={plusIcon} />}
              disableElevation
              style={{
                backgroundColor: "#E3E5E5",
                color: "#999999",
              }}
            >
              {t("MY_LEAVE_NEW_REQUEST")}
            </Button>
          </OcCustomTooltip>
        )}
      </div>
    </div>
  );
};

export default MyLeaveFilterBar;
